import type { QuietHours } from "./sync/types";
import { locale, t } from "./i18n";

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// clock is the weekday (0 is Sunday) and minute of the day at a time in a
// time zone.
function clock(t: Date, zone: string): { day: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(t);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return { day: weekdays.indexOf(get("weekday")), minute: Number(get("hour")) * 60 + Number(get("minute")) };
}

const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

// quietUntil is when the quiet hours one is in at a time end, or null if
// the time is outside them.
export function quietUntil(q: QuietHours | null | undefined, now = new Date()): Date | null {
  if (!q) return null;
  let c: { day: number; minute: number };
  try {
    c = clock(now, q.time_zone);
  } catch {
    return null;
  }
  const start = minutes(q.start);
  const end = minutes(q.end);
  const on = (day: number) => q.days.includes((day + 7) % 7);
  const left = (to: number) => new Date(Math.floor(now.getTime() / 60_000) * 60_000 + (to - c.minute) * 60_000);
  if (start < end) return on(c.day) && c.minute >= start && c.minute < end ? left(end) : null;
  // Past midnight: from start on a day it starts, or until end the day after.
  if (on(c.day) && c.minute >= start) return left(24 * 60 + end);
  if (on(c.day - 1) && c.minute < end) return left(end);
  return null;
}

// dayName is a weekday's short name (0 is Sunday) in the reader's language:
// "Mon", or "周一".
export const dayName = (d: number) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2026, 9, 18 + d));

// describeQuiet says when quiet hours are: "22:00–08:00, Mon–Fri".
export function describeQuiet(q: QuietHours): string {
  const days = [...q.days].sort();
  const which =
    days.length === 7
      ? t("every day")
      : days.join() === "1,2,3,4,5"
        ? t("weekdays")
        : days.join() === "0,6"
          ? t("weekends")
          : days.length === 6
            ? t("every day but {day}", { day: dayName([0, 1, 2, 3, 4, 5, 6].find((d) => !days.includes(d))!) })
            : new Intl.ListFormat(locale, { type: "conjunction", style: "narrow" }).format(days.map(dayName));
  return t("{start}–{end}, {days}", { start: q.start, end: q.end, days: which });
}
